// src/publicuser/components/learning/LearningNav.jsx
import React, { useState, useEffect } from 'react';
import '../../css/LearningNav.css';
import img from '../../../img/logo.png'

const LearningNav = ({ 
  categories, 
  selectedCategoryId, 
  onCategorySelect, 
  onSearch,
  searchQuery: externalSearchQuery,
  onSearchQueryChange,
  isLoading,
  // Posts data for the modal
  posts,
  onPostClick
}) => {
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Use external search query if provided, otherwise use local
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : localSearchQuery;
  const setSearchQuery = onSearchQueryChange || setLocalSearchQuery;

  // Close mobile menu when pressing escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch && searchQuery.trim()) {
      onSearch(searchQuery.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    if (onSearch) {
      onSearch('');
    }
  };

  const handleCategorySelect = (categoryId) => {
    onCategorySelect(categoryId);
    // Don't close modal here - let user see posts first
  };

  const handlePostClickAndClose = (post) => {
    if (onPostClick) {
      onPostClick(post);
    }
    setIsMobileMenuOpen(false); // Close modal after post selection
  };

return (
  <nav className="learn-nav">
    {/* First Row - Brand and Search */}
    <div className="learn-nav-row learn-nav-row-primary">
      <div className="learn-nav-container">
        
        {/* Brand Section */}
        <a href="/" className="learn-nav-brand-link" title="Back to Home">
          <div className="learn-nav-brand">
            <img 
              src={img}
              alt="Tech Shara Logo" 
              className="learn-brand-img-asset"
            />
            <span className="learn-brand-text">Learning Zone</span>
          </div>
        </a>
        
        <div className="learn-nav-search">
          <form onSubmit={handleSearch} className="learn-search-form">
            <div className="learn-search-wrapper">
              <span className="learn-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="learn-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="learn-search-clear"
                  onClick={handleClearSearch}
                >
                  ✕
                </button>
              )}
            </div>
            <button type="submit" className="learn-search-submit" disabled={isLoading}>
              {isLoading ? '...' : 'Search'}
            </button>
          </form>
        </div>
        
        {/* Mobile Menu Toggle - ONLY visible on mobile */}
        <div className="mobile-browse-container mobile-only">
  <button 
    className={`learn-mobile-menu-toggle ${isMobileMenuOpen ? 'learn-active' : ''}`}
    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
    aria-label="Browse courses"
  >
    <div className="menu-toggle-content">
      <span className="learn-hamburger"></span>
      <div className="menu-text-wrapper">
        <span className="menu-toggle-text">Browse</span>
        <span className="menu-toggle-subtext">Courses</span>
      </div>
      {/* Move pulse dot inside button */}
      <div className="browse-pulse-indicator">
        <span className="pulse-dot"></span>
        <span className="browse-tooltip">📚 Tap to explore all courses</span>
      </div>
    </div>
  </button>
</div>
      </div>
    </div>

    {/* Desktop Categories - Horizontal bar (only on desktop) */}
    <div className="learn-nav-row learn-nav-row-secondary desktop-only">
      <div className="learn-nav-container">
        <ul className="learn-nav-categories">
          <li>
            <button
              className={`learn-category-btn ${selectedCategoryId === 'all' ? 'learn-active' : ''}`}
              onClick={() => onCategorySelect('all')}
            >
              <span className="learn-category-icon">🎯</span>
              All
            </button>
          </li>
          {categories.map(category => (
            <li key={category.id}>
              <button
                className={`learn-category-btn ${selectedCategoryId === category.id ? 'learn-active' : ''}`}
                onClick={() => onCategorySelect(category.id)}
              >
                {category.icon && <span className="learn-category-icon">{category.icon}</span>}
                {category.name}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>

    {/* SINGLE MODAL - Categories + Posts */}
    {isMobileMenuOpen && (
      <>
        <div 
          className="learn-modal-overlay" 
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <div className="learn-modal">
          <div className="learn-modal-header">
            <h3>Browse Courses</h3>
            <button 
              className="learn-modal-close"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              ✕
            </button>
          </div>
          
          <div className="learn-modal-body">
            {/* Categories Section */}
            <div className="modal-categories-section">
              <h4>Categories</h4>
              <div className="modal-categories-list">
                <button
                  className={`modal-category-chip ${selectedCategoryId === 'all' ? 'active' : ''}`}
                  onClick={() => handleCategorySelect('all')}
                >
                  All
                </button>
                {categories.map(category => (
                  <button
                    key={category.id}
                    className={`modal-category-chip ${selectedCategoryId === category.id ? 'active' : ''}`}
                    onClick={() => handleCategorySelect(category.id)}
                  >
                    {category.icon && <span>{category.icon}</span>}
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Posts Section */}
            <div className="modal-posts-section">
              <h4>
                {selectedCategoryId === 'all' 
                  ? 'All Courses' 
                  : `${categories.find(c => c.id === selectedCategoryId)?.name || 'ALL'} Tutorials`
                }
              </h4>
              
              {posts && posts.length > 0 ? (
                <div className="modal-posts-list">
                  {posts.map(post => (
                    <div 
                      key={post.id}
                      className="modal-post-item"
                      onClick={() => handlePostClickAndClose(post)}
                    >
                      {post.image && (
                        <img src={post.image} alt={post.title} className="post-image" />
                      )}
                      <div className="post-content">
                        <h5>{post.title}</h5>
                        {post.description && (
                          <p>{post.description.substring(0, 80)}...</p>
                        )}
                        {post.lessons && (
                          <span className="post-meta">{post.lessons} lessons</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-posts">
                  <p>No courses found in this category</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    )}
  </nav>
);
};

export default LearningNav;